import React, { useEffect, useRef, useState } from 'react';
import Lenis from '@studio-freight/lenis';
import './bsaTheme.css';
import OpenMapsLink from "./OpenMapsLink";
import heroImg from './bhaisepatisportsacademy-assets/bhaisepati-sports-academy.webp';
import ground1Img from './bhaisepatisportsacademy-assets/bhaisepati-sports-academy-g1.webp';
import ground2Img from './bhaisepatisportsacademy-assets/bhaisepati-sports-academy-g2.webp';
import appMockup from './bhaisepatisportsacademy-assets/app-mockup.png';
import ongroundLogo from '../assets/branding/onground-powered-by-dark-bg.png';
import OngroundStoreIcon from '../OngroundStoreIcon';
import {
  resolveOngroundAppLink,
  resolveVenueSocialLink,
  resolveThemeArray,
  resolveThemeCopy,
  resolveThemeLogo,
  resolveThemeMedia,
  resolveThemeObject,
} from '../publicWebsiteContent';
import { resolveThemePaletteStyle } from '../themePalette';
import { isWebsiteSectionVisible } from '../themeDefinitions';
import usePublicContactEnquiry from '../usePublicContactEnquiry';
import PublicContactEnquiryStatus, { PublicContactHoneypot, PublicContactPhoneField } from '../PublicContactEnquiryStatus';

// ── Exact original fallbacks ─────────────────────────────────────────────────
const STATIC_COURTS = [
  {
    id: 1,
    img: ground1Img,
    name: 'BADMINTON COURT 1',
    sport: 'Badminton',
    price: 'NPR 1,500/hr',
    badge: 'COURT 1',
    desc: 'Professional Li-Ning synthetic flooring with international-standard markings. Bright LED floodlighting ensures perfect visibility for competitive play and training sessions.',
    features: ['Li-Ning Surface', 'LED Floodlights', 'Full Markings', 'Nets Included'],
  },
  {
    id: 2,
    img: ground2Img,
    name: 'BADMINTON COURT 2',
    sport: 'Badminton',
    price: 'NPR 1,500/hr',
    badge: 'COURT 2',
    desc: 'Spacious covered court with excellent ceiling height and ventilation. Ideal for doubles matches, casual play, and group training sessions.',
    features: ['High Ceiling', 'Great Ventilation', 'Full Markings', 'Nets Included'],
  },
];

const STATIC_STATS = [
  { n: 2, suffix: '', label: 'Premium Courts' },
  { n: 500, suffix: '+', label: 'Players / Month' },
  { n: 14, suffix: ' hrs', label: 'Open Daily' },
  { n: 1500, suffix: '/hr', label: 'Starting Price (NPR)' },
];

const STATIC_AMENITIES = [
  { icon: '🅿️', label: 'Free Parking' },
  { icon: '🚿', label: 'Washroom' },
  { icon: '💡', label: 'Floodlights' },
  { icon: '🏸', label: 'Equipment Rental' },
];

const STATIC_TESTIMONIALS = [
  {
    text: 'Best badminton courts in the Bhaisepati area. The Li-Ning flooring is top quality and the lighting is perfect for evening games. Highly recommended!',
    name: 'Suman Shrestha',
    role: 'Regular Player',
    rating: 5,
  },
  {
    text: 'I bring my kids here every weekend for coaching. The facility is clean, well-maintained, and the staff are very friendly. A great sporting environment.',
    name: 'Anita Maharjan',
    role: 'Parent & Player',
    rating: 5,
  },
  {
    text: 'We organized our corporate badminton tournament here and it was fantastic. Professional courts, great amenities, and smooth booking through the app.',
    name: 'Rajesh Thapa',
    role: 'Corporate Events',
    rating: 5,
  },
];

const STATIC_FAQS = [
  {
    q: 'How do I book a court?',
    a: 'You can book via the ONGROUND app 24/7, or call us directly at +977 9767470347. Walk-ins are also welcome based on availability.',
  },
  {
    q: 'What equipment is available for rent?',
    a: 'We offer badminton rackets and shuttlecocks for rent. Please ask at the reception for current rates and availability.',
  },
  {
    q: 'What are the opening hours?',
    a: 'We are open daily from 6:00 AM to 8:00 PM, including weekends and public holidays.',
  },
  {
    q: 'Is coaching available?',
    a: 'Yes! We have experienced badminton coaches available for private and group training sessions. Contact us to schedule.',
  },
  {
    q: 'Is the facility suitable for beginners?',
    a: 'Absolutely — our academy welcomes players of all skill levels, from complete beginners to competitive players.',
  },
  {
    q: 'What is the cancellation policy?',
    a: 'Free cancellation up to 3 hours before your slot via the ONGROUND app. Late cancellations may incur a partial charge.',
  },
];

const STATIC_MARQUEE_ITEMS = [
  'SMASH',
  'SERVE',
  'RALLY',
  'TRAIN',
  'COMPETE',
  'WIN',
  'PLAY',
  'DRIVE',
];

const BSA_THEME_ID = 'bhaisepati-sports-academy';
const STATIC_MAP_EMBED_URL =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3533.75!2d85.31!3d27.65!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjfCsDM5JzAwLjAiTiA4NcKwMTgnMzYuMCJF!5e0!3m2!1sen!2snp!4v1';

const FACILITY_PRESENTATION = Object.freeze({
  parking: { icon: '🅿️', label: 'Free Parking' },
  washroom: { icon: '🚿', label: 'Washroom' },
  changing_room: { icon: '👕', label: 'Changing Room' },
  lights: { icon: '💡', label: 'Floodlights' },
  cafeteria: { icon: '☕', label: 'Cafeteria' },
  equipment_rental: { icon: '🏸', label: 'Equipment Rental' },
});

const cleanText = (value) => String(value == null ? '' : value).trim();

const formatLabel = (value) =>
  cleanText(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const normalizeKey = (value) =>
  cleanText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const formatTime = (value, fallback) => {
  const input = cleanText(value);

  if (!/^\d{1,2}:\d{2}$/.test(input)) {
    return input || fallback;
  }

  const [hourText, minute] = input.split(':');
  const hour = Number(hourText);

  if (!Number.isFinite(hour)) {
    return fallback;
  }

  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute} ${period}`;
};

const getOpenHoursDuration = (openTime, closeTime) => {
  const parseMinutes = (value) => {
    const match = cleanText(value).match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;

    const hour = Number(match[1]);
    const minute = Number(match[2]);

    if (
      !Number.isInteger(hour) ||
      !Number.isInteger(minute) ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      return null;
    }

    return hour * 60 + minute;
  };

  const start = parseMinutes(openTime);
  const end = parseMinutes(closeTime);

  if (start == null || end == null) {
    return null;
  }

  const durationMinutes =
    end > start
      ? end - start
      : end === start
        ? 24 * 60
        : end + 24 * 60 - start;

  return Math.round(durationMinutes / 60);
};

const getVenuePhone = (venue) =>
  cleanText(venue?.contact?.e164) ||
  cleanText(venue?.contact?.nationalNumber) ||
  '';

const getVenueAddress = (venue) => {
  const address = cleanText(venue?.location?.address);
  if (address) return address;

  return [
    cleanText(venue?.location?.city),
    cleanText(venue?.location?.country || venue?.country),
  ]
    .filter(Boolean)
    .join(', ');
};

const getVenueLocality = (venue) =>
  cleanText(venue?.location?.address).split(',')[0] ||
  cleanText(venue?.location?.city) ||
  '';

const getSafeExternalUrl = (value, fallback = '#') => {
  const input = cleanText(value);

  if (!input || input === '#') {
    return fallback;
  }

  try {
    const parsed = new URL(input);

    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return fallback;
    }

    return parsed.toString();
  } catch {
    return fallback;
  }
};

const buildMapEmbedUrl = (venue, configuredUrl) => {
  const rawLatitude = venue?.location?.latitude;
  const rawLongitude = venue?.location?.longitude;
  const latitude = rawLatitude === null || rawLatitude === undefined || rawLatitude === ''
    ? Number.NaN
    : Number(rawLatitude);
  const longitude = rawLongitude === null || rawLongitude === undefined || rawLongitude === ''
    ? Number.NaN
    : Number(rawLongitude);
  const hasValidCoordinates =
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180;

  if (hasValidCoordinates) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(`${latitude},${longitude}`)}&z=15&output=embed`;
  }

  const safeConfiguredUrl = getSafeExternalUrl(configuredUrl, '');
  if (safeConfiguredUrl) {
    return safeConfiguredUrl;
  }

  const address = getVenueAddress(venue);
  return address
    ? `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed`
    : STATIC_MAP_EMBED_URL;
};

const getGroundImage = (ground) =>
  cleanText(ground?.mainImageUrl || ground?.imageUrl);

const getGroundSportLabel = (ground) =>
  formatLabel(
    ground?.sportName ||
      ground?.sport?.name ||
      ground?.type,
  );

const getGroundFeatures = (ground) => {
  const source = [ground?.features, ground?.facilities, ground?.amenities]
    .find((value) => Array.isArray(value) && value.length > 0);

  return Array.isArray(source)
    ? source
        .map((item) =>
          formatLabel(
            typeof item === 'string'
              ? item
              : item?.label || item?.name || item?.type,
          ),
        )
        .filter(Boolean)
    : [];
};

const formatGroundPrice = (ground, venue) => {
  const rawAmount =
    ground?.basePrice ??
    ground?.price ??
    ground?.pricing?.basePrice ??
    venue?.pricing?.basePrice;

  if (rawAmount === undefined || rawAmount === null || rawAmount === '') {
    return '';
  }

  const amount = Number(rawAmount);

  if (!Number.isFinite(amount) || amount < 0) {
    return '';
  }

  const currency = cleanText(
    ground?.currency ||
      ground?.pricing?.currency ||
      venue?.pricing?.currency,
  ).toUpperCase();

  const unit = cleanText(ground?.unit).toLowerCase();
  const unitSuffix = unit === 'slot' ? '/slot' : '/hr';

  return `${currency || 'NPR'} ${amount.toLocaleString('en-US')}${unitSuffix}`;
};

const normalizeRating = (value) => {
  const rating = Math.round(Number(value));

  return Number.isFinite(rating)
    ? Math.min(5, Math.max(1, rating))
    : 5;
};

const splitVenueBrand = (venueName) => {
  const parts = cleanText(venueName).split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return {
      primary: 'Bhaisepati',
      highlighted: 'Sports',
      secondary: 'Academy',
    };
  }

  return {
    primary: parts[0],
    highlighted: parts[1] || '',
    secondary: parts.slice(2).join(' '),
  };
};

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useScrollAnimation(disabled = false) {
  useEffect(() => {
    if (disabled) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('bsa-in-view');
          }
        });
      },
      { threshold: 0.1 },
    );

    document
      .querySelectorAll('[data-animate]')
      .forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [disabled]);
}

function useCounter(target, inView) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;

    let start = 0;
    let animationFrameId = 0;
    const duration = 1800;

    const step = (timestamp) => {
      if (!start) start = timestamp;

      const progress = Math.min((timestamp - start) / duration, 1);
      setCount(Math.floor(progress * target));

      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      }
    };

    animationFrameId = window.requestAnimationFrame(step);

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [inView, target]);

  return count;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function BsaTheme({
  venue = {},
  grounds = [],
  website = {},
  primaryColor = '#FFD700',
  accentColor = '#0A1628',
  heroImage: commonHeroImage = '',
  openBooking = () => {},
  previewMode = false,
}) {
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  const themePaletteStyle = resolveThemePaletteStyle(
    BSA_THEME_ID,
    primaryColor,
    accentColor,
  );

  const t = (key, fallback, apiValue = '') =>
    resolveThemeCopy(
      website,
      BSA_THEME_ID,
      key,
      fallback,
      apiValue,
    );

  const m = (key, fallback) =>
    resolveThemeMedia(
      website,
      BSA_THEME_ID,
      key,
      fallback,
    );

  const {
    values: contactForm,
    handleChange: handleContactChange,
    submit: handleContactSubmit,
    submitting: contactSubmitting,
    feedback: contactFeedback,
  } = usePublicContactEnquiry({
    venue,
    previewMode,
    successMessage: t('formSuccessMessage', "Message sent! We'll get back to you shortly."),
  });

  const social = resolveThemeObject(
    website,
    BSA_THEME_ID,
    'social',
    {},
  );

  const venueName = cleanText(venue?.name) || 'Bhaisepati Sports Academy';
  const brand = splitVenueBrand(venueName);
  const venueAddress = getVenueAddress(venue) || 'Karyabinayak, Kathmandu';
  const venueLocality = getVenueLocality(venue) || 'Karyabinayak';
  const venuePhone = getVenuePhone(venue) || '+977 9767470347';

  const rawOpenTime = cleanText(venue?.policies?.openTime) || '06:00';
  const rawCloseTime = cleanText(venue?.policies?.closeTime) || '20:00';
  const openingTime = formatTime(rawOpenTime, '6:00 AM');
  const closingTime = formatTime(rawCloseTime, '8:00 PM');
  const displayHours = `${openingTime} – ${closingTime}`;
  const openHoursDuration =
    getOpenHoursDuration(rawOpenTime, rawCloseTime) || STATIC_STATS[2].n;

  const venueGrounds = Array.isArray(grounds) ? grounds : [];
  const gallery = Array.isArray(venue?.media?.gallery)
    ? venue.media.gallery.filter(Boolean)
    : [];

  const courtCards = (venueGrounds.length > 0
    ? venueGrounds.map((ground, index) => {
        const fallback = STATIC_COURTS[index % STATIC_COURTS.length];
        const apiFeatures = getGroundFeatures(ground);

        return {
          id: cleanText(ground?._id || ground?.id) || `ground-${index + 1}`,
          ground,
          img:
            getGroundImage(ground) ||
            gallery[index] ||
            fallback.img,
          name: cleanText(ground?.name) || fallback.name,
          sport: getGroundSportLabel(ground) || fallback.sport,
          price: formatGroundPrice(ground, venue) || fallback.price,
          badge: `${t('courtBadgePrefix', 'COURT')} ${index + 1}`,
          desc: cleanText(ground?.description) || fallback.desc,
          features:
            apiFeatures.length > 0
              ? apiFeatures
              : fallback.features,
        };
      })
    : STATIC_COURTS.map((court, index) => ({
        ...court,
        badge: `${t('courtBadgePrefix', 'COURT')} ${index + 1}`,
        ground: null,
      })))
    .filter((court) => court.name && court.img);

  const uniqueSportLabels = Array.from(
    new Set(
      courtCards
        .map((court) => cleanText(court.sport))
        .filter(Boolean),
    ),
  );

  const groundCount = venueGrounds.length || STATIC_STATS[0].n;
  const sportCount = uniqueSportLabels.length || 1;

  const minimumGroundPrice = venueGrounds
    .map((ground) =>
      Number(
        ground?.basePrice ??
          ground?.price ??
          ground?.pricing?.basePrice,
      ),
    )
    .filter((amount) => Number.isFinite(amount) && amount >= 0)
    .sort((left, right) => left - right)[0];

  const venueBasePrice = Number(
    minimumGroundPrice ?? venue?.pricing?.basePrice,
  );

  const startingPrice =
    Number.isFinite(venueBasePrice) && venueBasePrice >= 0
      ? venueBasePrice
      : STATIC_STATS[3].n;

  const startingCurrency =
    cleanText(
      venueGrounds.find((ground) => {
        const amount = Number(
          ground?.basePrice ??
            ground?.price ??
            ground?.pricing?.basePrice,
        );

        return amount === minimumGroundPrice;
      })?.currency || venue?.pricing?.currency,
    ).toUpperCase() || 'NPR';

  const stats = [
    {
      n: groundCount,
      suffix: '',
      label: t('statsCourtsLabel', 'Premium Courts'),
    },
    venueGrounds.length > 0
      ? {
          n: sportCount,
          suffix: '',
          label: t(
            'statsSecondaryLabel',
            'Players / Month',
            'Sports Offered',
          ),
        }
      : {
          n: STATIC_STATS[1].n,
          suffix: STATIC_STATS[1].suffix,
          label: t('statsSecondaryLabel', STATIC_STATS[1].label),
        },
    {
      n: openHoursDuration,
      suffix: ' hrs',
      label: t('statsHoursLabel', 'Open Daily'),
    },
    {
      n: startingPrice,
      suffix: '/hr',
      label: t(
        'statsStartingLabel',
        'Starting Price (NPR)',
        `Starting Price (${startingCurrency})`,
      ),
    },
  ];

  const apiAmenities = Array.isArray(venue?.facilities)
    ? venue.facilities
        .map((facility) => {
          const key = normalizeKey(facility);
          if (!key) return null;

          return (
            FACILITY_PRESENTATION[key] || {
              icon: '✓',
              label: formatLabel(facility),
            }
          );
        })
        .filter(Boolean)
    : [];

  const amenities = apiAmenities.length > 0
    ? apiAmenities
    : STATIC_AMENITIES;

  const testimonials = resolveThemeArray(
    website,
    BSA_THEME_ID,
    'testimonials',
    STATIC_TESTIMONIALS,
  ).map((testimonial, index) => {
    const fallback =
      STATIC_TESTIMONIALS[index % STATIC_TESTIMONIALS.length];

    return {
      text: cleanText(testimonial?.text) || fallback.text,
      name: cleanText(testimonial?.name) || fallback.name,
      role:
        cleanText(testimonial?.role || testimonial?.sport) ||
        fallback.role,
      rating: normalizeRating(testimonial?.rating ?? fallback.rating),
    };
  });

  const apiFaqs = STATIC_FAQS.map((faq, index) => {
    if (index === 0) {
      return {
        ...faq,
        q: `How do I book a court at ${venueName}?`,
        a: getVenuePhone(venue)
          ? `You can book via the ONGROUND app 24/7, or call us directly at ${venuePhone}. Walk-ins are also welcome based on availability.`
          : faq.a,
      };
    }

    if (index === 2) {
      return {
        ...faq,
        a: `We are open daily from ${openingTime} to ${closingTime}, including weekends and public holidays.`,
      };
    }

    if (index === 5 && cleanText(venue?.policies?.cancellationPolicy)) {
      return {
        ...faq,
        a: cleanText(venue.policies.cancellationPolicy),
      };
    }

    return faq;
  });

  const faqs = resolveThemeArray(
    website,
    BSA_THEME_ID,
    'faqs',
    STATIC_FAQS,
    apiFaqs,
  ).map((faq, index) => {
    const fallback = STATIC_FAQS[index % STATIC_FAQS.length];

    return {
      q: cleanText(faq?.q || faq?.question) || fallback.q,
      a: cleanText(faq?.a || faq?.answer) || fallback.a,
    };
  });

  const apiMarqueeItems =
    venueGrounds.length > 0 && uniqueSportLabels.length > 0
      ? [
        ...uniqueSportLabels.map((sport) => sport.toUpperCase()),
        ...STATIC_MARQUEE_ITEMS,
      ]
    : [];

  const marqueeItems = resolveThemeArray(
    website,
    BSA_THEME_ID,
    'marqueeWords',
    STATIC_MARQUEE_ITEMS.map((word) => ({ word })),
    apiMarqueeItems.map((word) => ({ word })),
  )
    .map((item) =>
      cleanText(typeof item === 'string' ? item : item?.word),
    )
    .filter(Boolean);

  const navigation = [
    { id: 'courts', sectionKey: 'grounds', label: t('navCourts', 'Courts') },
    { id: 'about', sectionKey: 'about', label: t('navAbout', 'About') },
    { id: 'faq', sectionKey: 'faqs', label: t('navFaq', 'FAQ') },
    { id: 'contact', sectionKey: 'contact', label: t('navContact', 'Contact') },
  ].filter(({ sectionKey }) =>
    isWebsiteSectionVisible(website, sectionKey),
  );

  const apiCourtsDescription =
    venueGrounds.length > 0
      ? sportCount === 1
        ? `${groundCount} premium indoor ${(uniqueSportLabels[0] || 'sports').toLowerCase()} ${groundCount === 1 ? 'court is' : 'courts are'} available to book.`
        : `${groundCount} premium indoor courts across ${sportCount} sports are available to book.`
      : '';

  const resolvedHeroImage = m(
    'heroImageUrl',
    cleanText(commonHeroImage) ||
      cleanText(venue?.media?.bannerImage) ||
      gallery[0] ||
      heroImg,
  );

  const resolvedHeroFloatImage = m(
    'heroFloatImageUrl',
    getGroundImage(venueGrounds[1]) ||
      getGroundImage(venueGrounds[0]) ||
      gallery[1] ||
      ground2Img,
  );

  const resolvedAboutImage = m(
    'aboutImageUrl',
    getGroundImage(venueGrounds[0]) ||
      gallery[0] ||
      heroImg,
  );

  const resolvedAboutFloatImage = m(
    'aboutFloatImageUrl',
    getGroundImage(venueGrounds[1]) ||
      getGroundImage(venueGrounds[0]) ||
      gallery[1] ||
      ground1Img,
  );

  const resolvedVenueLogo = resolveThemeLogo(
    website,
    BSA_THEME_ID,
    '',
  );
  const resolvedAppImage = m('appImageUrl', appMockup);
  const resolvedPoweredLogo = m('poweredLogoUrl', ongroundLogo);

  const heroBadgeApi = venueLocality
    ? `Premium Badminton Facility · ${venueLocality}`
    : '';

  const resolvedHeroDescription = t(
    'heroDescription',
    "Lalitpur's premier racket sports academy with professional Li-Ning courts, world-class lighting, and a passionate sporting community.",
    cleanText(venue?.shortDescription || venue?.fullDescription),
  );

  const resolvedAboutDescription = t(
    'aboutDescription',
    "Bhaisepati Sports Academy is a premier racket sports venue in Lalitpur, offering well-maintained badminton courts for players of all skill levels. Whether you're training, playing competitively, or enjoying a casual match with friends, the academy provides a quality sporting environment in the heart of Bhaisepati.",
    cleanText(venue?.fullDescription || venue?.shortDescription),
  );

  const resolvedFooterTagline = t(
    'footerTagline',
    "Lalitpur's premier badminton destination. Two professional courts, one great community.",
    cleanText(venue?.shortDescription),
  );

  const appStoreUrl = resolveOngroundAppLink(
    social?.appStoreUrl,
    'appStore',
  );
  const googlePlayUrl = resolveOngroundAppLink(
    social?.googlePlayUrl,
    'googlePlay',
  );
  const mapEmbedUrl = buildMapEmbedUrl(venue, social?.mapEmbedUrl);

  const footerSocialItems = [
    {
      key: 'instagram',
      label: t('instagramLabel', 'Instagram'),
      href: resolveVenueSocialLink(
        social?.instagramUrl,
        'instagram',
      ),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
      ),
    },
    {
      key: 'facebook',
      label: t('facebookLabel', 'Facebook'),
      href: resolveVenueSocialLink(
        social?.facebookUrl,
        'facebook',
      ),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
      ),
    },
    {
      key: 'tiktok',
      label: t('tiktokLabel', 'TikTok'),
      href: resolveVenueSocialLink(
        social?.tiktokUrl,
        'tiktok',
      ),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"></path></svg>
      ),
    },
    {
      key: 'whatsapp',
      label: t('whatsappLabel', 'WhatsApp'),
      href: resolveVenueSocialLink(
        social?.whatsappUrl,
        'whatsapp',
      ),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
      ),
    },
  ].filter((item) => item.href);

  const canBook =
    website?.booking?.enabled !== false &&
    typeof openBooking === 'function';

  const openGroundBooking = (ground = null) => {
    if (previewMode) return;

    if (canBook) {
      openBooking(ground);
      return;
    }

    scrollTo('contact');
  };

  useScrollAnimation(previewMode);

  useEffect(() => {
    if (previewMode) return undefined;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (time) =>
        Math.min(1, 1.001 - Math.pow(2, -10 * time)),
    });

    let animationFrameId = 0;

    const raf = (time) => {
      lenis.raf(time);
      animationFrameId = window.requestAnimationFrame(raf);
    };

    animationFrameId = window.requestAnimationFrame(raf);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      lenis.destroy();
    };
  }, [previewMode]);

  useEffect(() => {
    if (previewMode) return undefined;

    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, [previewMode]);

  useEffect(() => {
    if (previewMode) {
      setStatsInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsInView(true);
      },
      { threshold: 0.3 },
    );

    if (statsRef.current) observer.observe(statsRef.current);

    return () => observer.disconnect();
  }, [previewMode]);

  const scrollTo = (id) => {
    if (previewMode) return;

    const target = document.getElementById(id);

    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (id === 'hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const dialCode = cleanText(venue?.contact?.dialCode);
  const formPhonePlaceholder = dialCode
    ? dialCode.startsWith('+')
      ? dialCode
      : `+${dialCode}`
    : '';

  return (
    <div
      className={`bsa-root ${previewMode ? 'bsa-theme-preview' : ''}`}
      style={themePaletteStyle}
    >
      {/* ── NAV ── */}
      <header className={`bsa-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="bsa-container bsa-nav-inner">
          <button className="bsa-logo" onClick={() => scrollTo('hero')}>
            {resolvedVenueLogo ? (
              <img
                className="bsa-logo-image"
                src={resolvedVenueLogo}
                alt={`${venueName} logo`}
              />
            ) : (
              <div className="bsa-logo-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/>
                </svg>
              </div>
            )}
            <div>
              <span className="bsa-logo-text">
                {brand.primary.toUpperCase()} <span>{brand.highlighted.toUpperCase()}</span>
              </span>
              <span className="bsa-logo-sub">{brand.secondary} · {venueLocality}</span>
            </div>
          </button>
          <nav className="bsa-nav-links">
            {navigation.map((link) => (
              <button key={link.id} className="bsa-nav-link" onClick={() => scrollTo(link.id)}>{link.label}</button>
            ))}
          </nav>
          <button className="bsa-btn-primary bsa-btn-sm" onClick={() => openGroundBooking()}>
            {t('navBookLabel', 'BOOK NOW')}
          </button>
        </div>
      </header>

      {/* ── HERO ── */}
      {isWebsiteSectionVisible(website, "hero") && (
      <section id="hero" className="bsa-hero">
        <div className="bsa-hero-glow" />
        <div className="bsa-hero-glow-2" />
        <div className="bsa-container bsa-hero-grid">
          <div className="bsa-hero-content">
            <div className="bsa-hero-badge" data-animate="fade-down">
              <span className="bsa-hero-badge-dot" />
              {t('heroBadge', 'Premium Badminton Facility · Karyabinayak', heroBadgeApi)}
            </div>
            <h1 className="bsa-hero-title" data-animate="fade-up">
              {t('heroTitleLineOne', 'YOUR COURT')}<br />
              <span className="bsa-text-gold">{t('heroTitleHighlight', 'IS READY.')}</span>
            </h1>
            <p className="bsa-hero-sub" data-animate="fade-up" style={{ '--delay': '0.15s' }}>
              {resolvedHeroDescription}
            </p>
            <div className="bsa-hero-actions" data-animate="fade-up" style={{ '--delay': '0.3s' }}>
              <button className="bsa-btn-primary bsa-btn-lg" onClick={() => openGroundBooking()}>
                {t('heroReserveLabel', 'RESERVE NOW')}
              </button>
              {isWebsiteSectionVisible(website, "about") && (
                <button className="bsa-btn-outline bsa-btn-lg" onClick={() => scrollTo('about')}>
                  {t('heroExploreLabel', 'EXPLORE')}
                </button>
              )}
            </div>
          </div>
          <div className="bsa-hero-images" data-animate="fade-left" style={{ '--delay': '0.2s' }}>
            <div className="bsa-hero-img-main">
              <img src={resolvedHeroImage} alt={`${venueName} Courts`} />
            </div>
            <div className="bsa-hero-img-float">
              <img src={resolvedHeroFloatImage} alt={`${venueName} Court View`} />
            </div>
            <div className="bsa-hero-pill">
              <div className="bsa-hero-pill-icon">🏸</div>
              <div>
                <div className="bsa-hero-pill-label">{t('heroPillLabel', 'LI-NING COURTS')}</div>
                <div className="bsa-hero-pill-sub">{t('heroPillDescription', 'Professional Grade')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── MARQUEE ── */}
      <div className="bsa-marquee-section">
        <div className="bsa-marquee-track">
          <div className="bsa-marquee-content">
            {[...marqueeItems, ...marqueeItems, ...marqueeItems, ...marqueeItems].map((item, index) => (
              <span key={`${item}-${index}`} className="bsa-marquee-item">
                <span className="bsa-marquee-dot" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── COURTS ── */}
      {isWebsiteSectionVisible(website, "grounds") && (
      <section id="courts" className="bsa-courts-section">
        <div className="bsa-container">
          <div className="bsa-section-header" data-animate="fade-up">
            <p className="bsa-eyebrow">{t('courtsEyebrow', 'AVAILABLE COURTS')}</p>
            <h2 className="bsa-section-title">{t('courtsTitle', 'CHOOSE YOUR COURT.')}</h2>
            <p className="bsa-section-sub">
              {t(
                'courtsDescription',
                'Two premium indoor badminton courts with professional-grade Li-Ning surfaces.',
                apiCourtsDescription,
              )}
            </p>
          </div>
          <div className="bsa-courts-list">
            {courtCards.map((court, index) => (
              <div key={court.id} className="bsa-court-card" data-animate="fade-up" style={{ '--delay': `${index * 0.1}s` }}>
                <div className="bsa-court-img-wrap">
                  <div className="bsa-court-img" style={{ backgroundImage: `url(${court.img})` }} />
                  <span className="bsa-court-badge">{court.badge}</span>
                </div>
                <div className="bsa-court-info">
                  <p className="bsa-court-sport">{court.sport}</p>
                  <h3 className="bsa-court-title">{court.name}</h3>
                  <div className="bsa-court-price">{court.price}</div>
                  <p className="bsa-court-desc">{court.desc}</p>
                  <div className="bsa-court-features">
                    {court.features.map((feature) => (
                      <span key={feature} className="bsa-court-feature">✓ {feature}</span>
                    ))}
                  </div>
                  <button className="bsa-btn-primary bsa-btn-sm" onClick={() => openGroundBooking(court.ground)}>
                    {t('courtBookLabel', 'BOOK THIS COURT')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── STATS ── */}
      {isWebsiteSectionVisible(website, "stats") && (
      <section className="bsa-stats-section" ref={statsRef}>
        <div className="bsa-container">
          <div className="bsa-stats-grid">
            {stats.map((stat, index) => (
              <StatCell key={`${stat.label}-${index}`} stat={stat} inView={statsInView} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── ABOUT ── */}
      {isWebsiteSectionVisible(website, "about") && (
      <section id="about" className="bsa-about-section">
        <div className="bsa-container">
          <div className="bsa-about-grid">
            <div className="bsa-about-images" data-animate="fade-right">
              <div className="bsa-about-img-main">
                <img src={resolvedAboutImage} alt={venueName} />
              </div>
              <div className="bsa-about-img-float">
                <img src={resolvedAboutFloatImage} alt={`${venueName} Court`} />
              </div>
              <div className="bsa-about-badge">
                <span className="bsa-about-badge-num">{t('aboutBadgeNumber', '#1')}</span>
                <span className="bsa-about-badge-text">
                  {t('aboutBadgeText', 'In Bhaisepati', venueLocality ? `In ${venueLocality}` : '')}
                </span>
              </div>
            </div>
            <div data-animate="fade-up">
              <p className="bsa-eyebrow">{t('aboutEyebrow', 'ABOUT THE ACADEMY')}</p>
              <h2 className="bsa-section-title">
                {t('aboutTitleLineOne', 'PREMIUM BADMINTON')}<br />
                {t('aboutTitleLineTwo', 'FACILITY')}
              </h2>
              <p className="bsa-about-desc">{resolvedAboutDescription}</p>
              <div className="bsa-amenities-grid">
                {amenities.map((amenity) => (
                  <div key={`${amenity.icon}-${amenity.label}`} className="bsa-amenity-chip">
                    <span className="bsa-amenity-icon">{amenity.icon}</span>
                    {amenity.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── TESTIMONIALS ── */}
      {isWebsiteSectionVisible(website, "testimonials") && (
      <section className="bsa-testimonials-section">
        <div className="bsa-container">
          <div className="bsa-section-header" data-animate="fade-up">
            <p className="bsa-eyebrow">{t('testimonialsEyebrow', 'PLAYER REVIEWS')}</p>
            <h2 className="bsa-section-title">{t('testimonialsTitle', 'WHAT PLAYERS SAY.')}</h2>
            <p className="bsa-section-sub">
              {t(
                'testimonialsDescription',
                'Hear from our growing community of badminton enthusiasts.',
                cleanText(venue?.location?.city)
                  ? `Hear from the sporting community around ${cleanText(venue.location.city)}.`
                  : '',
              )}
            </p>
          </div>
          <div className="bsa-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={`${testimonial.name}-${index}`} className="bsa-testimonial-card" data-animate="fade-up" style={{ '--delay': `${index * 0.1}s` }}>
                <div className="bsa-testimonial-stars">
                  {Array.from({ length: testimonial.rating }).map((_, starIndex) => (
                    <span key={starIndex} className="bsa-star">★</span>
                  ))}
                </div>
                <p className="bsa-testimonial-text">&ldquo;{testimonial.text}&rdquo;</p>
                <div className="bsa-testimonial-author">
                  <div className="bsa-author-avatar">{testimonial.name.charAt(0)}</div>
                  <div>
                    <div className="bsa-author-name">{testimonial.name}</div>
                    <div className="bsa-author-role">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ── FAQ ── */}
      {isWebsiteSectionVisible(website, "faqs") && (
      <section id="faq" className="bsa-faq-section">
        <div className="bsa-container">
          <div className="bsa-faq-grid">
            <div className="bsa-faq-left" data-animate="fade-right">
              <p className="bsa-eyebrow">{t('faqEyebrow', 'FAQ')}</p>
              <h2 className="bsa-section-title">{t('faqTitle', 'GOT QUESTIONS?')}</h2>
              <p className="bsa-faq-left-desc">
                {t(
                  'faqDescription',
                  'Everything you need to know about playing at Bhaisepati Sports Academy.',
                  `Everything you need to know about playing at ${venueName}.`,
                )}
              </p>
            </div>
            <div className="bsa-faq-list" data-animate="fade-up">
              {faqs.map((faq, index) => (
                <div key={`${faq.q}-${index}`} className={`bsa-faq-item ${openFaq === index ? 'open' : ''}`}>
                  <button className="bsa-faq-trigger" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                    {faq.q}
                    <span className="bsa-faq-icon">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </span>
                  </button>
                  <div className="bsa-faq-body"><p>{faq.a}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── CONTACT ── */}
      {isWebsiteSectionVisible(website, "contact") && (
      <section id="contact" className="bsa-contact-section">
        <div className="bsa-container">
          <div className="bsa-section-header" data-animate="fade-up">
            <p className="bsa-eyebrow">{t('contactEyebrow', 'GET IN TOUCH')}</p>
            <h2 className="bsa-section-title">{t('contactTitle', 'BOOK YOUR COURT.')}</h2>
            <p className="bsa-section-sub">{t('contactDescription', 'Ready to play? Reach out or fill the form below to reserve your slot.')}</p>
          </div>
          <div className="bsa-contact-wrapper" data-animate="fade-up" style={{ '--delay': '0.1s' }}>
            <div className="bsa-contact-grid">
              <div className="bsa-contact-left">
                <div>
                  <h3 className="bsa-contact-big-title">{t('contactPanelTitle', "LET'S PLAY.")}</h3>
                  <p className="bsa-contact-desc">{t('contactPanelDescription', "Reach out for bookings, coaching inquiries, or general information. We're here to help.")}</p>
                </div>
                <div className="bsa-contact-items">
                  <div className="bsa-contact-item">
                    <div className="bsa-contact-item-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    </div>
                    <div>
                      <p className="bsa-contact-item-label">{t('locationLabel', 'Location')}</p>
                      <p className="bsa-contact-item-val">{venueAddress}</p>
                    </div>
                  </div>
                  <div className="bsa-contact-item">
                    <div className="bsa-contact-item-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    </div>
                    <div>
                      <p className="bsa-contact-item-label">{t('phoneLabel', 'Phone')}</p>
                      <p className="bsa-contact-item-val">{venuePhone}</p>
                    </div>
                  </div>
                  <div className="bsa-contact-item">
                    <div className="bsa-contact-item-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    </div>
                    <div>
                      <p className="bsa-contact-item-label">{t('hoursLabel', 'Hours')}</p>
                      <p className="bsa-contact-item-val">{displayHours}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bsa-contact-right">
                <h4 className="bsa-contact-form-title">{t('formTitle', 'SEND A MESSAGE')}</h4>
                <p className="bsa-contact-form-sub">{t('formDescription', "We'll get back to you within 24 hours.")}</p>
                <form className="bsa-contact-form" onSubmit={handleContactSubmit}>
                  <PublicContactHoneypot value={contactForm.company} onChange={handleContactChange} />
                  <div className="bsa-form-row">
                    <div className="bsa-input-group">
                      <label className="bsa-input-label">{t('formNameLabel', 'Full Name')}</label>
                      <input className="bsa-input" name="name" type="text" autoComplete="name" maxLength={100} placeholder={t('formNamePlaceholder', 'Your name')} value={contactForm.name} onChange={handleContactChange} required />
                    </div>
                    <div className="bsa-input-group">
                      <label className="bsa-input-label">{t('formPhoneLabel', 'Phone')}</label>
                      <PublicContactPhoneField
                        inputClassName="bsa-input"
                        value={contactForm.phone}
                        countryCode={contactForm.phoneCountryCode}
                        onChange={handleContactChange}
                        placeholder={t('formPhonePlaceholder', '+977', formPhonePlaceholder)}
                      />
                    </div>
                  </div>
                  <div className="bsa-input-group">
                    <label className="bsa-input-label">{t('formEmailLabel', 'Email')}</label>
                    <input className="bsa-input" name="email" type="email" autoComplete="email" maxLength={254} placeholder={t('formEmailPlaceholder', 'your@email.com')} value={contactForm.email} onChange={handleContactChange} />
                  </div>
                  <div className="bsa-input-group">
                    <label className="bsa-input-label">{t('formMessageLabel', 'Message')}</label>
                    <textarea className="bsa-input" name="message" maxLength={1200} placeholder={t('formMessagePlaceholder', 'Tell us about your booking needs...')} value={contactForm.message} onChange={handleContactChange} required />
                  </div>
                  <button type="submit" className="bsa-btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={contactSubmitting}>
                    {contactSubmitting ? 'SENDING…' : t('formSubmitLabel', 'SEND MESSAGE')}
                  </button>
                  <PublicContactEnquiryStatus submitting={contactSubmitting} feedback={contactFeedback} comfortableSpacing />
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── MAP ── */}
      {isWebsiteSectionVisible(website, "location") && (
      <section className="bsa-map-section">
        <div className="bsa-container">
          <div className="bsa-map-wrapper" data-animate="fade-up" style={{ position: 'relative' }}>
            <iframe
              title={`${venueName} Location`}
              src={mapEmbedUrl}
              allowFullScreen
              loading="lazy"
            />
            <OpenMapsLink
              url={venue?.googleMapsUrl}
              latitude={venue?.location?.latitude}
              longitude={venue?.location?.longitude}
              venueName={venueName}
              label={t('openMapsLabel', 'Open in Maps')}
            />
          </div>
        </div>
      </section>
      )}

      {/* ── CTA / APP ── */}
      {isWebsiteSectionVisible(website, "app") && (
      <section className="bsa-cta-section">
        <div className="bsa-container">
          <div className="bsa-cta-grid">
            <div>
              <p className="bsa-eyebrow bsa-cta-eyebrow" data-animate="fade-up">{t('appEyebrow', 'DOWNLOAD THE APP')}</p>
              <h2 className="bsa-cta-title" data-animate="fade-up" style={{ '--delay': '0.05s' }}>
                {t('appTitleLineOne', 'BOOK COURTS')}<br />{t('appTitleLineTwo', 'ON THE GO.')}
              </h2>
              <p className="bsa-cta-desc" data-animate="fade-up" style={{ '--delay': '0.1s' }}>
                {t(
                  'appDescription',
                  'Use the ONGROUND app to reserve your court anytime, anywhere. View availability, manage bookings, and join the community.',
                  `Use the ONGROUND app to reserve your court at ${venueName} anytime, anywhere. View availability, manage bookings, and join the community.`,
                )}
              </p>
              <div className="bsa-cta-stores" data-animate="fade-up" style={{ '--delay': '0.15s' }}>
                <a href={appStoreUrl} className="bsa-cta-store-btn">
                  <OngroundStoreIcon
                    platform="appStore"
                    className="bsa-cta-store-icon"
                    monochromeColor="#ffffff"
                  />
                  <div>
                    <span className="bsa-cta-store-label">{t('appStoreSmallLabel', 'Download on')}</span>
                    <span className="bsa-cta-store-name">{t('appStoreMainLabel', 'APP STORE')}</span>
                  </div>
                </a>
                <a href={googlePlayUrl} className="bsa-cta-store-btn">
                  <OngroundStoreIcon
                    platform="googlePlay"
                    className="bsa-cta-store-icon"
                  />
                  <div>
                    <span className="bsa-cta-store-label">{t('googlePlaySmallLabel', 'Get it on')}</span>
                    <span className="bsa-cta-store-name">{t('googlePlayMainLabel', 'GOOGLE PLAY')}</span>
                  </div>
                </a>
              </div>
            </div>
            <div className="bsa-cta-image" data-animate="fade-left">
              <div className="bsa-cta-img-glow" />
              <img src={resolvedAppImage} alt="ONGROUND App" className="bsa-cta-img" />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ── FOOTER ── */}
      <footer className="bsa-footer">
        <div className="bsa-container">
          <div className="bsa-footer-grid">
            <div>
              <button className="bsa-logo" onClick={() => scrollTo('hero')} style={{ marginBottom: '0' }}>
                {resolvedVenueLogo ? (
                  <img
                    className="bsa-logo-image"
                    src={resolvedVenueLogo}
                    alt={`${venueName} logo`}
                  />
                ) : (
                  <div className="bsa-logo-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1-4-10z"/><line x1="2" y1="12" x2="22" y2="12"/>
                    </svg>
                  </div>
                )}
                <div>
                  <span className="bsa-logo-text" style={{ color: 'white' }}>
                    {brand.primary.toUpperCase()} <span>{brand.highlighted.toUpperCase()}</span>
                  </span>
                  <span className="bsa-logo-sub">{brand.secondary} · {venueLocality}</span>
                </div>
              </button>
              <p className="bsa-footer-tagline">{resolvedFooterTagline}</p>
            </div>
            <div>
              <h4 className="bsa-footer-col-title">{t('footerQuickLinksTitle', 'QUICK LINKS')}</h4>
              <ul className="bsa-footer-links">
                {navigation.map((link) => (
                  <li key={link.id}>
                    <button className="bsa-footer-link-btn" onClick={() => scrollTo(link.id)}>{link.label}</button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="bsa-footer-col-title">{t('footerContactTitle', 'CONTACT INFO')}</h4>
              <ul className="bsa-footer-links">
                <li><span className="bsa-footer-link-btn" style={{ cursor: 'default' }}>{venueAddress}</span></li>
                <li><span className="bsa-footer-link-btn" style={{ cursor: 'default' }}>{venuePhone}</span></li>
                <li><span className="bsa-footer-link-btn" style={{ cursor: 'default' }}>{displayHours}</span></li>
              </ul>
            </div>
            {footerSocialItems.length > 0 && (
              <div>
              <h4 className="bsa-footer-col-title">{t('footerSocialTitle', 'SOCIALS')}</h4>
              <ul className="bsa-footer-links">
                {footerSocialItems.map((item) => (
                  <li key={item.key}>
                    <a
                      href={item.href}
                      className="bsa-footer-link-btn"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    >
                      {item.icon}
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
              </div>
            )}
          </div>
          <div className="bsa-footer-bottom">
            <p className="bsa-footer-copy">&copy; {new Date().getFullYear()} {venueName}{t('footerCopyrightSuffix', '. All rights reserved.')}</p>
            <a
              className="bsa-footer-powered"
              href="https://getonground.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Visit Onground website"
            >
              {t('footerPoweredByLabel', 'Powered by')} <img src={resolvedPoweredLogo} alt={t('poweredLogoAlt', 'ONGROUND')} style={{ height: '22px', opacity: 0.8 }} />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCell({ stat, inView, delay }) {
  const count = useCounter(stat.n, inView);

  return (
    <div className="bsa-stat-cell" data-animate="fade-up" style={{ '--delay': `${delay}s` }}>
      <div className="bsa-stat-num">
        {count.toLocaleString()}<span className="bsa-stat-suffix">{stat.suffix}</span>
      </div>
      <div className="bsa-stat-label">{stat.label}</div>
    </div>
  );
}
